<template>
  <div class="document-details-page">
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="text-h6 mt-4">Loading document...</p>
    </div>

    <div v-else-if="document">
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-start mb-6">
        <div class="flex-grow-1">
          <div class="d-flex align-center mb-2">
            <v-btn
              icon
              variant="text"
              @click="$router.go(-1)"
              class="mr-2"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-chip
              v-if="document.category"
              :color="getCategoryColor(document.category)"
              variant="flat"
              class="mr-3"
            >
              {{ document.category.name }}
            </v-chip>
            <v-chip
              :color="getStatusColor(document.status)"
              variant="outlined"
              class="mr-3"
            >
              {{ getStatusText(document.status) }}
            </v-chip>
            <v-chip
              v-if="document.is_confidential"
              color="warning"
              variant="flat"
            >
              CONFIDENTIAL
            </v-chip>
          </div>
          
          <h1 class="text-h4 font-weight-bold text-primary mb-2">
            {{ document.title }}
          </h1>
          
          <div class="d-flex align-center text-medium-emphasis mb-4">
            <v-icon size="small" class="mr-2">mdi-account</v-icon>
            <span class="mr-4">Uploaded by {{ document.uploaded_by?.full_name || 'Unknown' }}</span>
            <v-icon size="small" class="mr-2">mdi-calendar</v-icon>
            <span class="mr-4">{{ formatDateTime(document.created_at) }}</span>
            <v-icon size="small" class="mr-2">mdi-file</v-icon>
            <span class="mr-4">{{ formatFileSize(document.file_size) }}</span>
            <v-icon size="small" class="mr-2">mdi-eye</v-icon>
            <span>{{ document.view_count || 0 }} views</span>
          </div>

          <div v-if="document.description" class="text-body-1 mb-4">
            {{ document.description }}
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <v-btn
            color="primary"
            @click="downloadDocument"
            prepend-icon="mdi-download"
          >
            Download
          </v-btn>
          
          <v-btn
            v-if="canEdit"
            color="secondary"
            variant="outlined"
            @click="editDocument"
            prepend-icon="mdi-pencil"
          >
            Edit
          </v-btn>

          <v-btn
            color="info"
            variant="outlined"
            @click="shareDocument"
            prepend-icon="mdi-share"
          >
            Share
          </v-btn>

          <v-menu>
            <template #activator="{ props }">
              <v-btn
                icon
                variant="text"
                v-bind="props"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="duplicateDocument">
                <v-list-item-title>Duplicate</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-content-copy</v-icon>
                </template>
              </v-list-item>
              <v-list-item @click="printDocument">
                <v-list-item-title>Print</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-printer</v-icon>
                </template>
              </v-list-item>
              <v-list-item @click="exportDocument">
                <v-list-item-title>Export</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-export</v-icon>
                </template>
              </v-list-item>
              <v-list-item @click="createLink">
                <v-list-item-title>Create Link</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-link</v-icon>
                </template>
              </v-list-item>
              <v-divider />
              <v-list-item v-if="canDelete" @click="deleteDocument" class="text-error">
                <v-list-item-title>Delete</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-delete</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-row>
        <!-- Main Content -->
        <v-col cols="12" md="8">
          <!-- Document Preview -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">Document Preview</span>
              <div class="d-flex align-center gap-2">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  @click="zoomOut"
                  :disabled="zoomLevel <= 50"
                >
                  <v-icon>mdi-magnify-minus</v-icon>
                </v-btn>
                <span class="text-body-2">{{ zoomLevel }}%</span>
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  @click="zoomIn"
                  :disabled="zoomLevel >= 200"
                >
                  <v-icon>mdi-magnify-plus</v-icon>
                </v-btn>
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  @click="toggleFullscreen"
                >
                  <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                </v-btn>
              </div>
            </v-card-title>
            
            <v-card-text class="pa-0">
              <div
                ref="previewContainer"
                class="document-preview"
                :class="{ 'fullscreen': isFullscreen }"
                :style="{ transform: `scale(${zoomLevel / 100})` }"
              >
                <!-- PDF Preview -->
                <div v-if="isPdf" class="pdf-preview">
                  <iframe
                    :src="getDocumentUrl()"
                    width="100%"
                    height="600"
                    frameborder="0"
                  ></iframe>
                </div>
                
                <!-- Image Preview -->
                <div v-else-if="isImage" class="image-preview text-center">
                  <v-img
                    :src="getDocumentUrl()"
                    :alt="document.title"
                    max-height="600"
                    contain
                  />
                </div>
                
                <!-- Text/Code Preview -->
                <div v-else-if="isText" class="text-preview">
                  <pre class="text-body-2">{{ documentContent }}</pre>
                </div>
                
                <!-- Video Preview -->
                <div v-else-if="isVideo" class="video-preview">
                  <video
                    :src="getDocumentUrl()"
                    controls
                    width="100%"
                    height="400"
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
                
                <!-- Office Document Preview -->
                <div v-else-if="isOfficeDocument" class="office-preview">
                  <div class="text-center pa-8">
                    <v-icon size="120" color="grey-lighten-2">
                      {{ getDocumentIcon(document.file_name) }}
                    </v-icon>
                    <h3 class="text-h6 font-weight-medium mt-4 mb-2">
                      {{ document.file_name }}
                    </h3>
                    <p class="text-body-1 text-medium-emphasis mb-4">
                      This document type cannot be previewed online
                    </p>
                    <v-btn
                      color="primary"
                      @click="downloadDocument"
                      prepend-icon="mdi-download"
                    >
                      Download to View
                    </v-btn>
                  </div>
                </div>
                
                <!-- Generic File Preview -->
                <div v-else class="generic-preview">
                  <div class="text-center pa-8">
                    <v-icon size="120" color="grey-lighten-2">
                      {{ getDocumentIcon(document.file_name) }}
                    </v-icon>
                    <h3 class="text-h6 font-weight-medium mt-4 mb-2">
                      {{ document.file_name }}
                    </h3>
                    <p class="text-body-1 text-medium-emphasis mb-4">
                      Preview not available for this file type
                    </p>
                    <v-btn
                      color="primary"
                      @click="downloadDocument"
                      prepend-icon="mdi-download"
                    >
                      Download File
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Comments Section -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">
                Comments ({{ comments.length }})
              </span>
              <v-btn
                v-if="isAuthenticated"
                size="small"
                color="primary"
                @click="showAddComment = !showAddComment"
                prepend-icon="mdi-plus"
              >
                Add Comment
              </v-btn>
            </v-card-title>
            
            <v-card-text>
              <!-- Add Comment Form -->
              <div v-if="showAddComment" class="add-comment-form mb-6">
                <v-textarea
                  v-model="newComment"
                  label="Add a comment..."
                  variant="outlined"
                  rows="3"
                  auto-grow
                  :rules="[rules.required]"
                />
                <div class="d-flex justify-end gap-2 mt-2">
                  <v-btn @click="cancelComment">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    :disabled="!newComment.trim()"
                    :loading="submittingComment"
                    @click="submitComment"
                  >
                    Post Comment
                  </v-btn>
                </div>
              </div>

              <!-- Comments List -->
              <div v-if="comments.length > 0" class="comments-list">
                <div
                  v-for="comment in comments"
                  :key="comment.id"
                  class="comment-item mb-4"
                >
                  <div class="d-flex align-start">
                    <v-avatar
                      :color="comment.author?.avatar ? undefined : 'primary'"
                      size="40"
                      class="mr-3"
                    >
                      <v-img
                        v-if="comment.author?.avatar"
                        :src="comment.author.avatar"
                        :alt="comment.author.full_name"
                      />
                      <span v-else class="text-subtitle-2 font-weight-bold">
                        {{ getInitials(comment.author?.full_name || 'User') }}
                      </span>
                    </v-avatar>
                    
                    <div class="flex-grow-1">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <div>
                          <span class="font-weight-medium">{{ comment.author?.full_name || 'Anonymous' }}</span>
                          <span class="text-body-2 text-medium-emphasis ml-2">
                            {{ formatRelativeTime(comment.created_at) }}
                          </span>
                        </div>
                        
                        <v-menu v-if="canEditComment(comment)" offset-y>
                          <template #activator="{ props }">
                            <v-btn
                              icon
                              size="small"
                              variant="text"
                              v-bind="props"
                            >
                              <v-icon size="16">mdi-dots-horizontal</v-icon>
                            </v-btn>
                          </template>
                          <v-list density="compact">
                            <v-list-item @click="editComment(comment)">
                              <v-list-item-title>Edit</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="deleteComment(comment)" class="text-error">
                              <v-list-item-title>Delete</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                      
                      <div v-if="editingComment?.id === comment.id" class="edit-comment-form">
                        <v-textarea
                          v-model="editCommentText"
                          variant="outlined"
                          rows="2"
                          auto-grow
                          hide-details
                        />
                        <div class="d-flex justify-end gap-2 mt-2">
                          <v-btn size="small" @click="cancelEditComment">Cancel</v-btn>
                          <v-btn
                            size="small"
                            color="primary"
                            :loading="updatingComment"
                            @click="updateComment"
                          >
                            Update
                          </v-btn>
                        </div>
                      </div>
                      
                      <div v-else class="comment-content">
                        <p class="text-body-1 mb-0" style="white-space: pre-wrap;">
                          {{ comment.content }}
                        </p>
                        
                        <div v-if="comment.updated_at !== comment.created_at" class="text-caption text-medium-emphasis mt-1">
                          Edited {{ formatRelativeTime(comment.updated_at) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Empty Comments State -->
              <div v-else class="text-center py-8">
                <v-icon size="64" color="grey-lighten-2">mdi-comment-outline</v-icon>
                <p class="text-body-1 text-medium-emphasis mt-2">
                  No comments yet. Be the first to comment!
                </p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Version History -->
          <v-card v-if="versions.length > 1">
            <v-card-title class="text-h6 font-weight-medium">
              Version History
            </v-card-title>
            <v-card-text>
              <v-timeline density="compact">
                <v-timeline-item
                  v-for="(version, index) in versions"
                  :key="version.id"
                  :dot-color="index === 0 ? 'primary' : 'grey'"
                  size="small"
                >
                  <template #icon>
                    <v-icon v-if="index === 0">mdi-star</v-icon>
                    <v-icon v-else>mdi-circle-outline</v-icon>
                  </template>
                  
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <div class="font-weight-medium">
                        Version {{ version.version_number }}
                        <v-chip v-if="index === 0" size="x-small" color="primary" class="ml-2">
                          Current
                        </v-chip>
                      </div>
                      <div class="text-body-2 text-medium-emphasis">
                        Updated by {{ version.updated_by?.full_name || 'Unknown' }} on {{ formatDateTime(version.created_at) }}
                      </div>
                      <div v-if="version.change_description" class="text-body-2 mt-1">
                        {{ version.change_description }}
                      </div>
                    </div>
                    <div class="d-flex align-center gap-1">
                      <v-btn
                        v-if="index !== 0"
                        size="small"
                        variant="outlined"
                        @click="compareVersions(version)"
                      >
                        Compare
                      </v-btn>
                      <v-btn
                        v-if="index !== 0"
                        size="small"
                        variant="text"
                        @click="downloadVersion(version)"
                      >
                        Download
                      </v-btn>
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="4">
          <!-- Document Information -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Document Information
            </v-card-title>
            <v-card-text>
              <div class="info-items">
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">File Name:</span>
                  <span class="font-weight-medium">{{ document.file_name }}</span>
                </div>
                <div v-if="document.category" class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Category:</span>
                  <v-chip
                    :color="getCategoryColor(document.category)"
                    size="small"
                    variant="flat"
                  >
                    {{ document.category.name }}
                  </v-chip>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Size:</span>
                  <span>{{ formatFileSize(document.file_size) }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Status:</span>
                  <v-chip
                    :color="getStatusColor(document.status)"
                    size="small"
                    variant="outlined"
                  >
                    {{ getStatusText(document.status) }}
                  </v-chip>
                </div>
                <div v-if="document.meeting" class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Meeting:</span>
                  <span class="font-weight-medium">{{ document.meeting.title }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Uploaded:</span>
                  <span>{{ formatDate(document.created_at) }}</span>
                </div>
                <div v-if="document.updated_at !== document.created_at" class="info-item d-flex justify-space-between">
                  <span class="text-medium-emphasis">Last Modified:</span>
                  <span>{{ formatDate(document.updated_at) }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Tags -->
          <v-card v-if="document.tags && document.tags.length > 0" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Tags
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="tag in document.tags"
                  :key="tag"
                  size="small"
                  variant="outlined"
                  @click="searchByTag(tag)"
                >
                  {{ tag }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Access Permissions -->
          <v-card v-if="isAdmin" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Access Permissions
            </v-card-title>
            <v-card-text>
              <div class="permissions-list">
                <div
                  v-for="permission in document.permissions || []"
                  :key="permission.id"
                  class="permission-item d-flex justify-space-between align-center mb-2"
                >
                  <div>
                    <div class="font-weight-medium">{{ permission.user?.full_name || permission.role }}</div>
                    <div class="text-body-2 text-medium-emphasis">{{ permission.permission_type }}</div>
                  </div>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="removePermission(permission)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
              
              <v-btn
                color="primary"
                variant="outlined"
                block
                @click="managePermissions"
                prepend-icon="mdi-plus"
              >
                Add Permission
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Related Documents -->
          <v-card>
            <v-card-title class="text-h6 font-weight-medium">
              Related Documents
            </v-card-title>
            <v-card-text>
              <div v-if="relatedDocuments.length > 0">
                <div
                  v-for="relatedDoc in relatedDocuments"
                  :key="relatedDoc.id"
                  class="related-doc-item d-flex align-center pa-2 mb-2"
                  @click="viewDocument(relatedDoc)"
                >
                  <v-icon
                    :color="getCategoryColor(relatedDoc.category)"
                    class="mr-3"
                  >
                    {{ getDocumentIcon(relatedDoc.file_name) }}
                  </v-icon>
                  <div class="flex-grow-1">
                    <div class="font-weight-medium text-body-2">{{ relatedDoc.title }}</div>
                    <div class="text-caption text-medium-emphasis">{{ relatedDoc.category?.name || 'No Category' }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <p class="text-body-2 text-medium-emphasis">No related documents</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Share Dialog -->
    <v-dialog v-model="showShareDialog" max-width="500">
      <v-card>
        <v-card-title>Share Document</v-card-title>
        <v-card-text>
          <v-text-field
            :model-value="shareUrl"
            label="Share URL"
            variant="outlined"
            readonly
            append-inner-icon="mdi-content-copy"
            @click:append-inner="copyShareUrl"
          />
          
          <div class="mt-4">
            <v-checkbox
              v-model="shareSettings.requireLogin"
              label="Require login to access"
            />
            <v-checkbox
              v-model="shareSettings.allowDownload"
              label="Allow download"
            />
            <v-text-field
              v-model="shareSettings.expiresAt"
              label="Expires At (Optional)"
              type="datetime-local"
              variant="outlined"
              density="compact"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showShareDialog = false">Close</v-btn>
          <v-btn color="primary" @click="generateShareLink">Generate Link</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Document</v-card-title>
        <v-card-text>
          Are you sure you want to delete this document? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error State -->
    <div v-if="!loading && !document" class="text-center py-12">
      <v-icon size="120" color="grey-lighten-2">mdi-file-document-alert</v-icon>
      <h3 class="text-h5 font-weight-medium mt-4 mb-2">Document Not Found</h3>
      <p class="text-body-1 text-medium-emphasis mb-4">
        The requested document could not be found or you don't have permission to view it.
      </p>
      <v-btn color="primary" @click="$router.push('/documents')">
        Back to Documents
      </v-btn>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/store/auth'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import api from '@/services/api'

export default {
  name: 'DocumentDetails',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()
    const authStore = useAuthStore()
    const { setBreadcrumbTitle } = useBreadcrumb()

    // State
    const loading = ref(true)
    const documentData = ref(null)
    const documentContent = ref('')
    const comments = ref([])
    const versions = ref([])
    const relatedDocuments = ref([])
    
    // User permissions
    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const currentUserId = computed(() => authStore.user?.id)
    const isAdmin = computed(() => authStore.user?.is_admin || false)
    const canEdit = computed(() => {
      if (!documentData.value) return false
      return isAdmin.value || documentData.value.uploaded_by?.id === currentUserId.value
    })
    const canDelete = computed(() => {
      if (!documentData.value) return false
      return isAdmin.value || documentData.value.uploaded_by?.id === currentUserId.value
    })

    // UI State
    const zoomLevel = ref(100)
    const isFullscreen = ref(false)
    const showAddComment = ref(false)
    const showShareDialog = ref(false)
    const showDeleteDialog = ref(false)

    // Comment state
    const newComment = ref('')
    const submittingComment = ref(false)
    const editingComment = ref(null)
    const editCommentText = ref('')
    const updatingComment = ref(false)

    // Share state
    const shareUrl = ref('')
    const shareSettings = ref({
      requireLogin: true,
      allowDownload: true,
      expiresAt: ''
    })

    // Other state
    const deleting = ref(false)
    const previewContainer = ref(null)

    // Validation rules
    const rules = {
      required: value => !!value || 'Field is required'
    }

    // Computed properties for file types
    const isPdf = computed(() => {
      return documentData.value?.file_name?.toLowerCase().endsWith('.pdf')
    })

    const isImage = computed(() => {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const fileName = documentData.value?.file_name?.toLowerCase() || ''
      return imageExtensions.some(ext => fileName.endsWith(ext))
    })

    const isText = computed(() => {
      const textExtensions = ['.txt', '.md', '.json', '.xml', '.csv', '.js', '.css', '.html']
      const fileName = documentData.value?.file_name?.toLowerCase() || ''
      return textExtensions.some(ext => fileName.endsWith(ext))
    })

    const isVideo = computed(() => {
      const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm']
      const fileName = documentData.value?.file_name?.toLowerCase() || ''
      return videoExtensions.some(ext => fileName.endsWith(ext))
    })

    const isOfficeDocument = computed(() => {
      const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx']
      const fileName = documentData.value?.file_name?.toLowerCase() || ''
      return officeExtensions.some(ext => fileName.endsWith(ext))
    })

    // Utility methods
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatRelativeTime = (dateString) => {
      if (!dateString) return 'Unknown'
      const date = new Date(dateString)
      const now = new Date()
      const diffInMinutes = (now - date) / (1000 * 60)
      
      if (diffInMinutes < 1) return 'Just now'
      if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)} minutes ago`
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
      if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)} days ago`
      
      return formatDate(dateString)
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }

    const getInitials = (name) => {
      if (!name) return 'U'
      return name.split(' ').map(part => part[0]).join('').toUpperCase()
    }

    // Status and category functions (matching Documents.vue)
    const getStatusColor = (status) => {
      if (!status) return 'grey'
      const colors = {
        active: 'success',
        draft: 'grey-darken-1', 
        review: 'warning',
        approved: 'success',
        archived: 'blue-grey',
        pending: 'orange',
        rejected: 'error'
      }
      return colors[status] || 'grey'
    }

    const getStatusText = (status) => {
      if (!status) return 'No Status'
      const statusText = {
        active: 'Active',
        draft: 'Draft',
        review: 'Under Review',
        approved: 'Approved',
        archived: 'Archived',
        pending: 'Pending',
        rejected: 'Rejected'
      }
      return statusText[status] || status.charAt(0).toUpperCase() + status.slice(1)
    }

    const getCategoryColor = (category) => {
      if (!category?.color) return 'primary'
      return category.color
    }

    const getDocumentIcon = (fileName) => {
      if (!fileName) return 'mdi-file'
      const ext = fileName.split('.').pop()?.toLowerCase()
      const icons = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        ppt: 'mdi-file-powerpoint-box',
        pptx: 'mdi-file-powerpoint-box',
        jpg: 'mdi-file-image',
        jpeg: 'mdi-file-image',
        png: 'mdi-file-image',
        gif: 'mdi-file-image',
        mp4: 'mdi-file-video',
        avi: 'mdi-file-video',
        txt: 'mdi-file-document-outline',
        md: 'mdi-file-document-outline'
      }
      return icons[ext] || 'mdi-file'
    }

    const getDocumentUrl = () => {
      if (!documentData.value) return ''
      return `${api.defaults.baseURL}/documents/${documentData.value.id}/content`
    }

    // UI Actions
    const zoomIn = () => {
      if (zoomLevel.value < 200) {
        zoomLevel.value += 25
      }
    }

    const zoomOut = () => {
      if (zoomLevel.value > 50) {
        zoomLevel.value -= 25
      }
    }

    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value
      if (isFullscreen.value) {
        document.documentElement.requestFullscreen?.()
      } else {
        document.exitFullscreen?.()
      }
    }

    // Document Actions
    const downloadDocument = async () => {
      try {
        const response = await api.get(`/documents/${documentData.value.id}/download`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', documentData.value.file_name)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        toast.success('Document downloaded successfully')
      } catch (error) {
        console.error('Error downloading document:', error)
        toast.error('Failed to download document')
      }
    }

    const editDocument = () => {
      router.push(`/documents/${documentData.value.id}/edit`)
    }

    const shareDocument = () => {
      shareUrl.value = `${window.location.origin}/documents/${documentData.value.id}/shared`
      showShareDialog.value = true
    }

    const duplicateDocument = () => {
      router.push({
        path: '/documents/create',
        query: { duplicate: documentData.value.id }
      })
    }

    const printDocument = () => {
      window.print()
    }

    const exportDocument = () => {
      toast.info('Export functionality coming soon')
    }

    const createLink = () => {
      shareDocument()
    }

    const deleteDocument = () => {
      showDeleteDialog.value = true
    }

    const confirmDelete = async () => {
      deleting.value = true
      try {
        await api.delete(`/documents/${documentData.value.id}`)
        toast.success('Document deleted successfully')
        router.push('/documents')
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('Failed to delete document')
      } finally {
        deleting.value = false
        showDeleteDialog.value = false
      }
    }

    // Comment Actions
    const canEditComment = (comment) => {
      return isAdmin.value || comment.author?.id === currentUserId.value
    }

    const submitComment = async () => {
      if (!newComment.value.trim()) return
      
      submittingComment.value = true
      try {
        const response = await api.post(`/documents/${documentData.value.id}/comments`, {
          content: newComment.value.trim()
        })
        
        comments.value.unshift(response.data.comment)
        newComment.value = ''
        showAddComment.value = false
        toast.success('Comment added successfully')
      } catch (error) {
        console.error('Error adding comment:', error)
        toast.error('Failed to add comment')
      } finally {
        submittingComment.value = false
      }
    }

    const cancelComment = () => {
      newComment.value = ''
      showAddComment.value = false
    }

    const editComment = (comment) => {
      editingComment.value = comment
      editCommentText.value = comment.content
    }

    const updateComment = async () => {
      if (!editCommentText.value.trim()) return
      
      updatingComment.value = true
      try {
        await api.put(`/documents/${documentData.value.id}/comments/${editingComment.value.id}`, {
          content: editCommentText.value.trim()
        })
        
        const index = comments.value.findIndex(c => c.id === editingComment.value.id)
        if (index !== -1) {
          comments.value[index].content = editCommentText.value.trim()
          comments.value[index].updated_at = new Date().toISOString()
        }
        
        cancelEditComment()
        toast.success('Comment updated successfully')
      } catch (error) {
        console.error('Error updating comment:', error)
        toast.error('Failed to update comment')
      } finally {
        updatingComment.value = false
      }
    }

    const cancelEditComment = () => {
      editingComment.value = null
      editCommentText.value = ''
    }

    const deleteComment = async (comment) => {
      try {
        await api.delete(`/documents/${documentData.value.id}/comments/${comment.id}`)
        const index = comments.value.findIndex(c => c.id === comment.id)
        if (index !== -1) {
          comments.value.splice(index, 1)
        }
        toast.success('Comment deleted successfully')
      } catch (error) {
        console.error('Error deleting comment:', error)
        toast.error('Failed to delete comment')
      }
    }

    // Other Actions
    const copyShareUrl = () => {
      navigator.clipboard.writeText(shareUrl.value)
      toast.success('Share URL copied to clipboard')
    }

    const generateShareLink = async () => {
      try {
        const response = await api.post(`/documents/${documentData.value.id}/share`, shareSettings.value)
        shareUrl.value = response.data.share_url
        toast.success('Share link generated')
      } catch (error) {
        console.error('Error generating share link:', error)
        toast.error('Failed to generate share link')
      }
    }

    const searchByTag = (tag) => {
      router.push(`/documents?tag=${encodeURIComponent(tag)}`)
    }

    const viewDocument = (doc) => {
      router.push(`/documents/${doc.id}`)
    }

    const managePermissions = () => {
      toast.info('Permission management coming soon')
    }

    const removePermission = (permission) => {
      toast.info('Permission removal coming soon')
    }

    const compareVersions = (version) => {
      toast.info('Version comparison coming soon')
    }

    const downloadVersion = (version) => {
      toast.info('Version download coming soon')
    }

    // Data loading
    const loadDocumentData = async () => {
      const documentId = route.params.id
      if (!documentId) {
        toast.error('Invalid document ID')
        router.push('/documents')
        return
      }

      loading.value = true
      try {
        // Load document details
        const documentResponse = await api.get(`/documents/${documentId}`)
        documentData.value = documentResponse.data.document
        setBreadcrumbTitle(documentData.value.title)

        // Load comments
        try {
          const commentsResponse = await api.get(`/documents/${documentId}/comments`)
          comments.value = commentsResponse.data.comments || []
        } catch (error) {
          console.error('Error loading comments:', error)
          comments.value = []
        }

        // Load versions (if supported)
        try {
          const versionsResponse = await api.get(`/documents/${documentId}/versions`)
          versions.value = versionsResponse.data.versions || []
        } catch (error) {
          console.error('Error loading versions:', error)
          versions.value = []
        }

        // Load related documents (if supported)
        try {
          const relatedResponse = await api.get(`/documents/${documentId}/related`)
          relatedDocuments.value = relatedResponse.data.documents || []
        } catch (error) {
          console.error('Error loading related documents:', error)
          relatedDocuments.value = []
        }

        // Load text content if applicable
        if (isText.value) {
          try {
            const contentResponse = await api.get(`/documents/${documentId}/content`, {
              responseType: 'text'
            })
            documentContent.value = contentResponse.data
          } catch (error) {
            console.error('Error loading document content:', error)
            documentContent.value = 'Failed to load content'
          }
        }

        // Track document view
        try {
          await api.post(`/documents/${documentId}/view`)
        } catch (error) {
          console.error('Error tracking view:', error)
        }
      } catch (error) {
        console.error('Error loading document:', error)
        toast.error('Failed to load document')
        documentData.value = null
      } finally {
        loading.value = false
      }
    }

    // Handle fullscreen changes
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        isFullscreen.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadDocumentData()
      document.addEventListener('fullscreenchange', handleFullscreenChange)
    })

    onUnmounted(() => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    })

    return {
      // State
      loading,
      document: documentData,
      documentContent,
      comments,
      versions,
      relatedDocuments,
      isAuthenticated,
      currentUserId,
      canEdit,
      canDelete,
      isAdmin,

      // UI State
      zoomLevel,
      isFullscreen,
      showAddComment,
      showShareDialog,
      showDeleteDialog,

      // Comment state
      newComment,
      submittingComment,
      editingComment,
      editCommentText,
      updatingComment,

      // Share state
      shareUrl,
      shareSettings,

      // Other state
      deleting,
      previewContainer,
      rules,

      // Computed
      isPdf,
      isImage,
      isText,
      isVideo,
      isOfficeDocument,

      // Utility methods
      formatDate,
      formatDateTime,
      formatRelativeTime,
      formatFileSize,
      getInitials,
      getStatusColor,
      getStatusText,
      getCategoryColor,
      getDocumentIcon,
      getDocumentUrl,

      // UI Actions
      zoomIn,
      zoomOut,
      toggleFullscreen,

      // Document Actions
      downloadDocument,
      editDocument,
      shareDocument,
      duplicateDocument,
      printDocument,
      exportDocument,
      createLink,
      deleteDocument,
      confirmDelete,

      // Comment Actions
      canEditComment,
      submitComment,
      cancelComment,
      editComment,
      updateComment,
      cancelEditComment,
      deleteComment,

      // Other Actions
      copyShareUrl,
      generateShareLink,
      searchByTag,
      viewDocument,
      managePermissions,
      removePermission,
      compareVersions,
      downloadVersion
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.document-details-page {
  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .text-medium-emphasis {
    opacity: 0.7;
  }

  .document-preview {
    transition: transform 0.3s ease;
    transform-origin: top left;
    
    &.fullscreen {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: white;
      z-index: 9999;
      overflow: auto;
    }

    .pdf-preview,
    .image-preview,
    .text-preview,
    .video-preview,
    .office-preview,
    .generic-preview {
      min-height: 400px;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 8px;
      overflow: hidden;
    }

    .text-preview {
      padding: 16px;
      background-color: #f8f9fa;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 14px;
      line-height: 1.5;
      overflow: auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  .comment-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-bottom: 16px;

    &:last-child {
      border-bottom: none;
    }
  }

  .comment-content {
    line-height: 1.6;
  }

  .add-comment-form,
  .edit-comment-form {
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    padding: 16px;
  }

  .info-items {
    .info-item {
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);

      &:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }
    }
  }

  .related-doc-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }

  .permission-item {
    padding: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
  }

  .v-timeline {
    .v-timeline-item {
      padding-bottom: 16px;
    }
  }

  // Print styles
  @media print {
    .v-btn,
    .comment-item .v-menu,
    .document-preview .d-flex {
      display: none !important;
    }
  }

  // Dark theme adjustments
  .v-theme--dark {
    .text-preview {
      background-color: #1e1e1e;
      color: #d4d4d4;
    }
  }
}
</style>